// src/components/ui/TagPill.jsx - Small colored tag pill
import React from 'react';
import { X } from 'lucide-react';
import { cn } from '../../utils/cn';

/**
 * TagPill - Displays a tag as a small colored pill
 *
 * @param {object} tag - Tag object with id, name, color
 * @param {string} size - 'sm' (default) or 'md'
 * @param {boolean} removable - Show X button to remove
 * @param {function} onRemove - Callback when X is clicked
 * @param {function} onClick - Callback when pill is clicked
 * @param {string} className - Additional classes
 */
const TagPill = ({
  tag,
  size = 'sm',
  removable = false,
  onRemove,
  onClick,
  className
}) => {
  if (!tag) return null;

  const handleRemove = (e) => {
    e.stopPropagation();
    onRemove?.(tag);
  };

  const handleClick = (e) => {
    if (onClick) {
      e.stopPropagation();
      onClick(tag);
    }
  };

  // Generate contrasting text color based on background
  const getTextColor = (hexColor) => {
    if (!hexColor) return 'white';
    const hex = hexColor.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5 ? '#1a1a1a' : '#ffffff';
  };

  const bgColor = tag.color || '#6366F1';
  const textColor = getTextColor(bgColor);

  return (
    <span
      onClick={handleClick}
      className={cn(
        "inline-flex items-center gap-1 rounded-full font-medium transition-opacity",
        size === 'sm' && "px-2 py-0.5 text-[10px]",
        size === 'md' && "px-2.5 py-1 text-xs",
        onClick && "cursor-pointer hover:opacity-80",
        className
      )}
      style={{
        backgroundColor: bgColor,
        color: textColor
      }}
    >
      <span className="truncate max-w-[80px]">{tag.name}</span>
      {removable && onRemove && (
        <button
          onClick={handleRemove}
          className="flex-shrink-0 hover:opacity-70 transition-opacity"
          type="button"
        >
          <X className={cn(
            size === 'sm' && "w-2.5 h-2.5",
            size === 'md' && "w-3 h-3"
          )} />
        </button>
      )}
    </span>
  );
};

/**
 * TagPillList - Displays a list of tags with optional overflow indicator
 *
 * @param {array} tags - Array of tag objects (from taggings, each has .tag property)
 * @param {number} maxVisible - Max tags to show before "+N" (default: 3)
 * @param {string} size - 'sm' or 'md'
 * @param {boolean} removable - Show X buttons
 * @param {function} onRemove - Callback when tag is removed
 * @param {function} onOverflowClick - Callback when "+N" is clicked
 * @param {string} className - Additional classes for container
 */
export const TagPillList = ({
  tags = [],
  maxVisible = 3,
  size = 'sm',
  removable = false,
  onRemove,
  onOverflowClick,
  className
}) => {
  if (!tags || tags.length === 0) return null;

  // Extract tag data - handle both direct tags and tagging objects
  const tagData = tags.map(t => t.tag || t);

  const visibleTags = tagData.slice(0, maxVisible);
  const overflowCount = tagData.length - maxVisible;

  return (
    <div className={cn("flex flex-wrap items-center gap-1", className)}>
      {visibleTags.map((tag) => (
        <TagPill
          key={tag.id}
          tag={tag}
          size={size}
          removable={removable}
          onRemove={onRemove}
        />
      ))}
      {overflowCount > 0 && (
        <span
          onClick={onOverflowClick}
          className={cn(
            "inline-flex items-center rounded-full bg-muted text-muted-foreground font-medium",
            size === 'sm' && "px-1.5 py-0.5 text-[10px]",
            size === 'md' && "px-2 py-1 text-xs",
            onOverflowClick && "cursor-pointer hover:bg-muted/80"
          )}
        >
          +{overflowCount}
        </span>
      )}
    </div>
  );
};

export default TagPill;
